import { getIslandRegionFromArea } from 'alakai-aloha-shared';

/**
 * Normalize Hawaiian place names by removing diacritical marks
 * This allows filtering to work with both 'Lihue' and 'Līhuʻe' spellings
 */
export function normalizeHawaiianName(name) {
  if (!name) return name;
  
  return name
    // Remove macrons (ā, ē, ī, ō, ū)
    .replace(/[āăàáâãäå]/gi, 'a')
    .replace(/[ēĕèéêëė]/gi, 'e')
    .replace(/[īĭìíîïį]/gi, 'i')
    .replace(/[ōŏòóôõöø]/gi, 'o')
    .replace(/[ūŭùúûüų]/gi, 'u')
    // Remove ʻokina (glottal stop)
    .replace(/[ʻ''`]/g, '')
    // Normalize whitespace
    .trim();
}

/**
 * Check if two Hawaiian place names are equivalent (ignoring diacritics)
 */
export function areHawaiianNamesEquivalent(name1, name2) {
  if (!name1 || !name2) return false;
  return normalizeHawaiianName(name1.toLowerCase()) === normalizeHawaiianName(name2.toLowerCase());
}

/**
 * Generate a URL-safe slug from a Hawaiian place name
 * This should match the format used in Sanity CMS slugs
 */
export function generateHawaiianSlug(name) {
  if (!name) return '';
  
  return normalizeHawaiianName(name)
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
    .replace(/\s+/g, '-')     // Replace spaces with hyphens
    .replace(/-+/g, '-')      // Replace multiple consecutive hyphens with single
    .replace(/^-|-$/g, '');   // Remove leading/trailing hyphens
}

// Fisher–Yates shuffle ───────────────
export function shuffle(array) {
  const a = array.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Generic filter helper that works for either entity type.
 *
 *  • Restaurants: region, serviceType, priceRange, cuisineCategory
 *  • Activities : region, category, cost, difficulty
 *
 * Pass the complete filters object from your UI; any unused keys
 * are safely ignored for the other entity.
 */
export function applyFilters(
  items,
  {
    // shared
    region = [],
    searchText = '',

    // restaurant-only
    serviceType = [],
    priceRange = [],
    cuisineCategory = [],

    // activity-only
    category = [],
    cost = [],
    difficulty = []
  }
) {
  const normalizedText = normalizeHawaiianName(searchText || '').toLowerCase();
  const normalizeField = (value) => normalizeHawaiianName(String(value || '')).toLowerCase();

  return items.filter(item => {
    /* ── Region (shared) ───────────────────────────────────────── */
    const itemRegion =
      item.region ||
      getIslandRegionFromArea(item.area, item.island || undefined, 'Other') ||
      'Other';
    const matchesRegion = region.length 
      ? region.some(filterRegion => 
          areHawaiianNamesEquivalent(filterRegion, itemRegion)
        )
      : true;

    /* ── Detect entity type ───────────────────────────────────── */
    const isRestaurant = 'serviceType' in item; // restaurants have this field
    const isLodging = !isRestaurant && 'priceRange' in item; // lodgings have priceRange but not serviceType
    const isActivity = !isRestaurant && !isLodging; // activities have neither

    /* ── Restaurant-specific checks ───────────────────────────── */
    const matchesService   = isRestaurant
      ? (serviceType.length   ? serviceType.includes(item.serviceType) : true)
      : true;

    const matchesPrice     = (isRestaurant || isLodging)
      ? (priceRange.length    ? priceRange.includes(item.priceRange)   : true)
      : true;

    const cc               = isRestaurant && Array.isArray(item.cuisineCategory)
      ? item.cuisineCategory
      : [];

    const matchesCuisine   = isRestaurant
      ? (cuisineCategory.length ? cuisineCategory.some(c => cc.includes(c)) : true)
      : true;

    /* ── Activity-specific checks ─────────────────────────────── */
    const activityCategoryValues = isActivity
      ? [
          item.category,
          item.displayCategory,
          item.activityTypeLabel,
          item.activityType,
          item.legacyCategoryLabel,
          item.legacyCategory
        ].filter(Boolean).map(value => String(value).toLowerCase())
      : [];

    const matchesCategory  = isActivity
     ? (category.length
         ? category
             .map(c => String(c).toLowerCase())
             .some(c => activityCategoryValues.includes(c))
         : true)
      : true;

    const matchesCost      = isActivity
     ? (cost.length
         ? cost
             .map(c => c.toLowerCase())
             .includes((item.cost || '').toLowerCase())
         : true)
      : true;

    const matchesDifficulty = isActivity
     ? (difficulty.length
         ? difficulty
             .map(d => d.toLowerCase())
             .includes((item.difficulty || '').toLowerCase())
         : true)
      : true;

    /* ── Free-text search (shared) ────────────────────────────── */
    const inText = !normalizedText ||
      normalizeField(item.name).includes(normalizedText) ||
      normalizeField(item.shortDescription).includes(normalizedText) ||
      // extra searchable fields
      (isRestaurant && normalizeField(cc.join(' ')).includes(normalizedText)) ||
      (isActivity && [
        item.category,
        item.displayCategory,
        item.activityTypeLabel,
        item.inventoryClassLabel,
        item.difficulty,
        Array.isArray(item.experienceTags) ? item.experienceTags.join(' ') : ''
      ].filter(Boolean).map(normalizeField).join(' ').includes(normalizedText)) ||
      (isLodging && item.features && Array.isArray(item.features) && 
        normalizeField(item.features.join(' ')).includes(normalizedText));

    /* ── Final decision ───────────────────────────────────────── */
    return (
      matchesRegion &&
      matchesService &&
      matchesPrice &&
      matchesCuisine &&
      matchesCategory &&
      matchesCost &&
      matchesDifficulty &&
      inText
    );
  });
}
